import React, { FC, useEffect, useState, useRef, SyntheticEvent } from 'react'
import { makeStyles } from '@material-ui/core'
import { isMobile, isSafari } from 'react-device-detect'
import ReactPlayer from 'react-player'
import screenfull from 'screenfull'
import classnames from 'classnames'

interface IVideoProps {
  checkVideoTime?: (player: any) => void
  onReady?: (e: ReactPlayer) => void
  onPlay?: () => void
  onEnded?: () => void
  startInFullscreen?: boolean
  posterSrc?: string
  videoSrc: string
  trackSrc?: string
  autoPlay?: boolean
  disableControls?: boolean
  disableContextMenu?: boolean
  autoLoop?: boolean
  setPlayTime?: (value: number) => void
  seekTo?: number
  hideSeekBar?: boolean
  onTimeUpdate?: (e: SyntheticEvent) => void
}

export const Video: FC<IVideoProps> = ({
  checkVideoTime,
  posterSrc,
  videoSrc,
  onReady,
  onPlay,
  trackSrc,
  autoPlay,
  disableControls,
  disableContextMenu,
  autoLoop,
  onEnded,
  setPlayTime,
  seekTo,
  startInFullscreen,
  hideSeekBar,
  onTimeUpdate
}) => {
  const classes = useStyles()
  const ref = useRef<ReactPlayer | null>(null)

  const [videoAccessible, setVideoAccessible] = useState<boolean>(true)
  const [playing, setPlaying] = useState<boolean>(false)
  const [controls] = useState<boolean>(!disableControls ?? false)
  const [loop] = useState<boolean>(autoLoop ?? false)

  const onEndedFunc = () => {
    if (onEnded) {
      onEnded()
    }
  }

  const onProgress = e => {
    if (setPlayTime) {
      setPlayTime(e.playedSeconds.toFixed(2))
    }
  }

  useEffect(() => {
    const player = ref.current?.getInternalPlayer()
    //setShowContextMenu(!disableContextMenu ?? false)
    if (player && disableContextMenu) {
      player.oncontextmenu = e => {
        e.preventDefault()
      }
    }
  }, [disableContextMenu])

  useEffect(() => {
    setVideoAccessible(false)
    setTimeout(() => {
      setVideoAccessible(true)
    }, 0)
  }, [videoSrc])

  return (
    <>
      {/* If the video file could not be found, we display the image in place of the video player w/ poster */}
      {!videoAccessible ? (
        <img src={posterSrc} alt='Video Placeholder' className={classes.vidPlaceholderImg} />
      ) : (
        <div className={classnames(classes.playerWrapper, 'video-player-wrapper')}>
          <ReactPlayer
            className={classnames(classes.reactPlayer, hideSeekBar && classes.reactPlayerNoSeekBar)}
            ref={ref}
            url={videoSrc}
            playing={playing}
            controls={controls}
            onProgress={onProgress}
            onPlay={() => {
              setPlaying(true)
              onPlay?.()
            }}
            onClickPreview={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onReady={(e: ReactPlayer) => {
              if (startInFullscreen && screenfull.isEnabled && ref) {
                screenfull.request(ref.current?.getInternalPlayer() as HTMLVideoElement)
              }
              // Autoplay is blocked on mobile devices
              if (autoPlay && !isMobile && !isSafari) {
                setPlaying(true)
              }
              onReady?.(e)
            }}
            onEnded={onEndedFunc}
            loop={loop}
            light={autoPlay && !isMobile && !isSafari ? undefined : posterSrc}
            width='100%'
            height='100%'
            volume={0.5}
            playsinline={true}
            {...(onTimeUpdate ? { config: { file: { attributes: { onTimeUpdate } } } } : {})}
          />
        </div>
      )}
    </>
  )
}

const useStyles = makeStyles({
  videoSrcAccessibilityCheckerElem: {
    display: 'none'
  },
  vidPlaceholderImg: {
    outline: 'none',
    width: '100%'
  },
  playerWrapper: {
    position: 'relative',
    paddingTop: '56.25%',
    '& video': {
      outline: 'none'
    }
  },
  reactPlayer: {
    position: 'absolute',
    left: 0,
    top: 0,
    '& .react-player__preview': {
      outline: 'none'
    },
    '& video::-webkit-media-controls-panel': {
      backgroundImage: `linear-gradient(transparent, transparent) !important`
    }
  },
  reactPlayerNoSeekBar: {
    '& video::-webkit-media-controls-timeline': {
      display: 'none'
    }
  }
})
